import {cn, openInNewWindow} from '@/lib/utils'
import React, {useRef, useState} from 'react'
import {useOnClickOutside} from '../../hooks/useOnClickOutsideHook'
import {IWuAppHeaderHelpMenu} from '../../types/WuAppHeaderHelpMenu'

export interface IWuAppHeaderHelpProps {
  menuList?: IWuAppHeaderHelpMenu[]
  dir?: 'ltr' | 'rtl'
}

export const WuAppHeaderHelp: React.FC<IWuAppHeaderHelpProps> = ({
  menuList,
  dir,
}) => {
  const DEFAULT_MENU_LIST: IWuAppHeaderHelpMenu[] = [
    {
      icon: 'wm-info',
      title: 'Help Center',
      subTitle: 'Knowledge Base',
      link: 'https://www.questionpro.com/help/',
    },
    {
      icon: 'wm-calendar-month',
      title: 'Free Online Training',
      subTitle: 'Webinar',
      link: 'https://www.questionpro.com/training',
    },
    {
      icon: 'wm-local-fire-department',
      title: 'Updates',
      subTitle: "What's new!",
      link: 'https://www.questionpro.com/updates/',
    },
    {
      icon: 'wm-chat',
      title: 'Live Chat',
      subTitle: 'Support',
      link: 'https://www.questionpro.com/a/liveperson.do?mode=launch&pageMode=quickChat',
      target: 'window',
    },
  ]

  const dropdownRef = useRef<HTMLUListElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  useOnClickOutside([dropdownRef, buttonRef], () => {
    setIsOpen(false)
  })

  const toggleDropdown = (): void => {
    setIsOpen(!isOpen)
  }

  return (
    <div className="dropdown-container">
      <ul>
        <li
          className={cn('flex relative', isOpen && 'open')}
          aria-label="help-dropdown"
        >
          <button
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-full border border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-soft focus-visible:ring-offset-2 focus-visible:ring-offset-gray-switcher text-white transition-colors hover:bg-white hover:text-gray-switcher wu-app-header-help',
              isOpen && 'bg-white text-gray-switcher',
            )}
            onClick={e => {
              e.preventDefault()
              toggleDropdown()
            }}
            ref={buttonRef}
          >
            <span className="wm-question-mark text-base"></span>
          </button>
          <div
            className={cn(
              'dropdown-menu',
              {open: isOpen},
              dir === 'rtl' ? '-left-4 top-[38px]' : '-right-[5px] top-[38px]',
            )}
          >
            <ul ref={dropdownRef}>
              {(menuList || DEFAULT_MENU_LIST).map((item, index) => (
                <li
                  key={index}
                  className="p-0 w-[191px] hover:bg-[#ededed]"
                  dir={dir}
                >
                  {item.target === 'window' ? (
                    <a
                      tabIndex={0}
                      rel="noopener noreferrer"
                      onClick={(): void => openInNewWindow(item.link)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          openInNewWindow(item.link)
                        }
                      }}
                      className="block w-full h-full px-4 py-2 hover:bg-gray-20 focus-visible:border-none focus-visible:bg-gray-20 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0 cursor-pointer"
                    >
                      <LinkItem item={item} dir={dir} />
                    </a>
                  ) : (
                    <a
                      href={item.link}
                      target="_blank"
                      className="block w-full h-full px-4 py-2 hover:bg-gray-20 focus-visible:border-none focus-visible:bg-gray-20 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0"
                      rel="noreferrer"
                    >
                      <LinkItem item={item} dir={dir} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </li>
      </ul>
    </div>
  )
}

const LinkItem: React.FC<{
  item: IWuAppHeaderHelpMenu
  dir?: 'ltr' | 'rtl'
}> = ({item, dir}) => (
  <div className="flex items-start gap-3">
    <span className={`${item.icon} text-xl text-gray-subtle`}></span>
    <div
      className={cn('flex flex-col gap-[2px]', dir === 'rtl' && 'text-right')}
    >
      <span className="text-xs text-gray-subtle">{item.subTitle}</span>
      <p className="text-sm text-blue-q">{item.title}</p>
    </div>
  </div>
)
